import { Component } from '@theme/component';

/**
 * DAVIDsTEA tabbed product information.
 *
 * The markup is a real ARIA tablist, so this only has to move the selection: one tab carries
 * aria-selected="true" and tabindex="0", every other tab is false and -1, and the matching
 * panel is the only one without `hidden`.
 *
 * Roving tabindex rather than making all tabs focusable: the WAI-ARIA tabs pattern puts one
 * stop in the page tab order for the whole row, and the arrow keys move between tabs. A row of
 * four separately tabbable buttons makes a keyboard user press Tab four times to reach the
 * panel underneath.
 *
 * The first paint is already correct — Liquid renders the first tab selected and hides the
 * rest — so nothing here runs before an interaction, and the band works with JavaScript off
 * apart from switching tabs.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends {Component<Refs>}
 */
export class DtTabbedInfo extends Component {
  // No required refs, deliberately. This element drives itself off the ARIA roles among its
  // descendants rather than off named refs — `refs.tablist` is never read. Declaring one
  // required makes the component throw from connectedCallback on every render that has no tab
  // row, which its own markup documents as valid: a product with a single format draws the
  // element and no tablist. An uncaught MissingRefError there stops the rest of the setup and
  // surfaces on the product page console.

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    super.disconnectedCallback();
  }

  /** @returns {HTMLButtonElement[]} */
  get #tabs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target)?.closest('[role="tab"]');
    if (!tab) return;
    this.#select(/** @type {HTMLButtonElement} */ (tab), false);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = /** @type {HTMLElement} */ (event.target)?.closest('[role="tab"]');
    if (!tab) return;

    const tabs = this.#tabs;
    const index = tabs.indexOf(/** @type {HTMLButtonElement} */ (tab));
    if (index === -1) return;

    let next = -1;
    switch (event.key) {
      case 'ArrowRight':
        next = (index + 1) % tabs.length;
        break;
      case 'ArrowLeft':
        next = (index - 1 + tabs.length) % tabs.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    // Only now, once the key is one this component handles — an early preventDefault would
    // swallow Tab and the arrow keys a screen reader uses to read the panel.
    event.preventDefault();
    this.#select(tabs[next], true);
  };

  /**
   * @param {HTMLButtonElement} tab
   * @param {boolean} moveFocus - true when the arrow keys drove the change, so focus follows
   */
  #select(tab, moveFocus) {
    const tabs = this.#tabs;
    if (!tabs.includes(tab)) return;

    for (const candidate of tabs) {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.tabIndex = selected ? 0 : -1;

      const panelId = candidate.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (panel instanceof HTMLElement) panel.hidden = !selected;
    }

    if (moveFocus) tab.focus();
  }
}

if (!customElements.get('dt-tabbed-info')) {
  customElements.define('dt-tabbed-info', DtTabbedInfo);
}
